import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import Chart from 'chart.js/auto';
import axios from 'axios';
import {
  Visibility as VisibilityIcon,
  AccessTime as AccessTimeIcon,
  CalendarMonth as CalendarMonthIcon,
  PageviewOutlined as PageviewIcon
} from '@mui/icons-material';
import { LoaderIcon } from 'lucide-react';
import { API, ANALYTICS } from "../../config/env";

// Styled components for the stats section
const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  width: 100%;
`;

const StatBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1.25rem;
  flex: 1;
  margin-right: 15px;
  border-radius: 12px;
  background: #FFF;
  border: 1px solid #f0f0f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;
  height: 120px;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  &:last-child {
    margin-right: 0;
  }
`;

const StatTitle = styled.span`
  color: #71717A;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.125rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const StatValue = styled.span`
  color: #18181B;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 2rem;
  font-style: normal;
  font-weight: 700;
  line-height: 2.25rem;
`;

const StatUnit = styled.span`
  color: #71717A;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: 4px;
`;

const PercentageChange = styled.div`
  color: ${props => props.isPositive ? '#00a651' : '#EF4444'};
  background-color: ${props => props.isPositive ? 'rgba(0, 166, 81, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.25rem;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 16px;

  &::before {
    content: '${props => props.isPositive ? '↑' : '↓'}';
    margin-right: 4px;
  }
`;

// Styled components for the chart section
const ChartContainer = styled.div`
  background: #FFF;
  border-radius: 10px;
  border: 0.01px solid #e0ebeb;
  box-shadow: 6px 6px 54px 0px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  margin-bottom: 30px;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ChartTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const TimeFilterContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const TimeFilterButton = styled.button`
  padding: 8px 16px;
  border-radius: 5px;
  background: ${props => props.active ? '#F0F4FF' : '#FFF'};
  border: 1px solid ${props => props.active ? '#4880FF' : '#e0e0e0'};
  color: ${props => props.active ? '#4880FF' : '#666'};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#F0F4FF' : '#f5f5f5'};
  }
`;

const MonthLabelsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
`;

const MonthLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 0.75rem;
  color: #666;
`;
const hoursToMinutesConverter = (hours) => {
  hours = (hours * 60 ).toFixed(2);
  return hours;
}

// Main component
const VideoEngagementStats = () => {
  const [timeFilter, setTimeFilter] = useState('12 Months');
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [statsValues, setStatsValues] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  console.log(analyticsData, 'analyticsData');
  // Mock data for stats
  const statsData = [
    {
      title: 'TOTAL VIEWS',
      value: statsValues ? statsValues.play_count : <LoaderIcon className='animate-spin' />,
      percentageChange: '',
      isPositive: true,
      icon: <VisibilityIcon sx={{ color: '#4880FF', fontSize: 24 }} />
    },
    {
      title: 'VIEWS IN LAST 30 DAYS',
      value: analyticsData ? analyticsData.views_analytics.last_30_days_views : <LoaderIcon className='animate-spin' />,
      percentageChange: analyticsData ? `${analyticsData.views_analytics.percentage_change_from_30_days_to_previous_30days}%` : '',
      isPositive: analyticsData ? analyticsData.views_analytics.percentage_change_from_30_days_to_previous_30days >= 0 : true,
      icon: <CalendarMonthIcon sx={{ color: '#4880FF', fontSize: 24 }} />
    },
    {
      title: 'TIME WATCHED',
      value: statsValues ? hoursToMinutesConverter(statsValues.hours_watched): <LoaderIcon className='animate-spin' />,
      unit: 'min',
      percentageChange: '',
      isPositive: true,
      icon: <AccessTimeIcon sx={{ color: '#4880FF', fontSize: 24 }} />
    },
    {
      title: 'PAGE VISITS',
      value: statsValues ? statsValues.load_count: <LoaderIcon className='animate-spin' />,
      percentageChange: '',
      isPositive: true,
      icon: <PageviewIcon sx={{ color: '#4880FF', fontSize: 28 }} />
    },
  ];

  // Generate months for chart labels
  const generateMonthLabels = (numMonths) => {
    const months = [];
    const currentDate = new Date();

    for (let i = numMonths - 1; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setMonth(currentDate.getMonth() - i);
      months.push(date.toLocaleString('default', { month: 'short' }));
    }

    return months;
  };

  // Generate day labels for the 30-day view
  const generateDayLabels = () => {
    const days = [];
    const currentDate = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() - i);
      // Format as "Jan 1", "Jan 2", etc.
      const day = date.getDate();
      const month = date.toLocaleString('default', { month: 'short' });
      days.push(`${month} ${day}`);
    }

    return days;
  };

  // Generate random data for the chart
  const generateChartData = (numPoints) => {
    const data = [];
    let value = 100;

    for (let i = 0; i < numPoints; i++) {
      // Random walk algorithm to create realistic looking data
      value += Math.random() * 50 - 25;
      value = Math.max(50, Math.min(350, value)); // Keep values between 50 and 350
      data.push(value);
    }

    return data;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Wistia stats
        const wistiaResponse = await axios.get(`${API.CORE}/api/v1/video-analytics/fetch-wistia-project-stats/${ANALYTICS.WISTIA_PROJECT_ID}`);
        setStatsValues(wistiaResponse.data);

        // Fetch university analytics
        const analyticsResponse = await axios.get(`${API.CORE}/api/v1/video-analytics/top/analytics/university/boston`);
        console.log('Full API Response:', analyticsResponse);
       
        
        if (analyticsResponse.data) {
          setAnalyticsData(analyticsResponse.data[0]);
        } else {
          console.error('No data received from analytics API');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response) {
          console.error('Error Response:', error.response.data);
          console.error('Error Status:', error.response.status);
        }
      }
    };
    fetchData();
  }, []);

  // Add a separate useEffect to monitor analyticsData changes
  useEffect(() => {
    console.log('Analytics Data Updated:', analyticsData);
    if (analyticsData) {
      console.log('Available Data:', {
        hasViewsAnalytics: !!analyticsData.views_analytics,
        hasDailyEngagement: !!analyticsData.daily_engagement,
        dailyEngagementLength: analyticsData.daily_engagement?.length
      });
    }
  }, [analyticsData]);

  // Process daily engagement data for chart
  const processEngagementData = (data, filterType) => {
    if (!data) return [];

    const today = new Date();
    let filteredData = data.daily_engagement;

    if (filterType === '30 Days') {
      filteredData = filteredData.slice(-30);
    } else if (filterType === '6 Months') {
      filteredData = filteredData.slice(-180);
    } else {
      filteredData = filteredData.slice(-365);
    }

    return filteredData.map(day => ({
      date: day.date,
      views: day.play_count
    }));
  };

  // Initialize and update chart
  useEffect(() => {
    if (chartRef.current && analyticsData) {
      // Destroy previous chart instance if it exists
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const processedData = processEngagementData(analyticsData, timeFilter);
      const labels = processedData.map(item => {
        const date = new Date(item.date);
        return timeFilter === '30 Days' 
          ? `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()}`
          : date.toLocaleString('default', { month: 'short' });
      });

      const viewsData = processedData.map(item => item.views);

      // Create new chart
      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: 'Views',
            data: viewsData,
            fill: true,
            backgroundColor: 'rgba(72, 128, 255, 0.1)',
            borderColor: '#4880FF',
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 6,
            pointBackgroundColor: '#4880FF',
            pointHoverBackgroundColor: '#4880FF',
            pointBorderWidth: 0,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              backgroundColor: '#FFF',
              titleColor: '#333',
              bodyColor: '#333',
              borderColor: '#e0e0e0',
              borderWidth: 1,
              padding: 10,
              displayColors: false,
              callbacks: {
                title: function(tooltipItems) {
                  return tooltipItems[0].label;
                },
                label: function(context) {
                  return `${context.parsed.y.toFixed(0)} views`;
                }
              }
            }
          },
          scales: {
            x: {
              display: false,
            },
            y: {
              display: false,
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [timeFilter, analyticsData]);

  return (
    <>
      <StatsContainer>
        {statsData.map((stat, index) => (
          <StatBox key={index}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <StatTitle style={{ display: 'flex', alignItems: 'center', height: '24px' }}>{stat.title}</StatTitle>
              {/* <div style={{ display: 'flex', alignItems: 'center', height: '24px' }}>
                {stat.icon}
              </div> */}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <StatValue>{stat.value}</StatValue>
                  {stat.unit && <StatUnit>{stat.unit}</StatUnit>}
                </div>
              </div>

              {stat.percentageChange && (
                <PercentageChange isPositive={stat.isPositive}>
                  {stat.percentageChange}
                </PercentageChange>
              )}
            </div>
          </StatBox>
        ))}
      </StatsContainer>

      <ChartContainer>
        <ChartHeader>
          <ChartTitle>Views</ChartTitle>
          <TimeFilterContainer>
            <TimeFilterButton
              active={timeFilter === '12 Months'}
              onClick={() => setTimeFilter('12 Months')}
            >
              12 Months
            </TimeFilterButton>
            <TimeFilterButton
              active={timeFilter === '6 Months'}
              onClick={() => setTimeFilter('6 Months')}
            >
              6 Months
            </TimeFilterButton>
            <TimeFilterButton
              active={timeFilter === '30 Days'}
              onClick={() => setTimeFilter('30 Days')}
            >
              30 Days
            </TimeFilterButton>
          </TimeFilterContainer>
        </ChartHeader>
        <div style={{ height: '250px', position: 'relative' }}>
          <canvas ref={chartRef}></canvas>
        </div>
        <MonthLabelsContainer>
          {timeFilter === '30 Days'
            ? generateDayLabels().filter((_, i) => i % 5 === 0 || i === 29).map((day, index) => (
                <MonthLabel key={index}>{day}</MonthLabel>
              ))
            : generateMonthLabels(timeFilter === '12 Months' ? 12 : 6).map((month, index) => (
                <MonthLabel key={index}>{month}</MonthLabel>
              ))
          }
        </MonthLabelsContainer>
      </ChartContainer>
    </>
  );
};

export default VideoEngagementStats;
